import { useEffect, useMemo, useRef, useState } from "react";
import {
  RequestGetAmazonItemsListRequest,
  RequestUpdateAmazonItemsStatusRequest,
  ResponseAmazonItems,
  ResponseGetAmazonItemsListResponse,
} from "../../services/OpenAPI";
import { useApiGetAmazonItems } from "../api/useApiGetAmazonItems";
import { useApiUpdateAmazonItemsStatus } from "../api/useApiUpdateAmazonItemsStatus";
import { NeedsReviewItem } from "../../components/Table/NeedsReviewTable";
import { formatDate } from "../../utils/dateFormat";
import { toDisplayFnsku } from "../../utils/fnsku";
import { loginUserSelector } from "../../recoil/selectors/loginUserSelector";
import { isGeneralUser, isNotGeneralUser } from "../../constants/roles";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { userSearchOptionsSelector } from "../../recoil/selectors/userSearchOptionsSelector";
import { pendingPackagingUpdatedFlagState } from "../../recoil/atoms/pendingPackaging";
import { Status } from "../../constants/status";
import { needsReviewUpdatedFlagState } from "../../recoil/atoms/needsReviewAtoms";
import { PrintLabelItem } from "../../types/PrintLabelItem";
import {
  ExpirationLabelItem,
  openPrintExpirationLabelWindow,
  openPrintLabelsWindow,
} from "../../utils/printLagel";
import { format } from "date-fns";
import { Order } from "../../components/pendingReceipt/PendingReceiptTable";

export type SearchGenreOption = {
  id: string;
  label: string;
};

export const useNeedsReview = () => {
  const searchGenres: SearchGenreOption[] = [
    { id: "janCode", label: "JAN" },
    { id: "asin", label: "ASIN" },
    { id: "itemName", label: "商品名" },
    { id: "fnsku", label: "FNSKU" },
    { id: "sku", label: "SKU" },
  ];
  const inputRef = useRef<HTMLInputElement>(null);

  const { fetchAmazonItems } = useApiGetAmazonItems();
  const { updateAmazonItemsStatus } = useApiUpdateAmazonItemsStatus();
  const loginUser = useRecoilValue(loginUserSelector);
  const userSearchOptions = useRecoilValue(userSearchOptionsSelector);
  const needsReviewUpdatedFlag = useRecoilValue(needsReviewUpdatedFlagState);
  const setNeedsReviewUpdatedFlag = useSetRecoilState(
    needsReviewUpdatedFlagState
  );
  const resetNeedsReviewUpdatedFlag = useResetRecoilState(
    needsReviewUpdatedFlagState
  );

  const [needsReviewList, setNeedsReviewList] = useState<NeedsReviewItem[]>([]);
  const [totalListCount, setTotalListCount] = useState(0);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [searchKeyword, setSearchKeyword] = useState("");
  const [janCode, setJanCode] = useState("");
  const [asin, setAsin] = useState("");
  const [itemName, setItemName] = useState("");
  const [fnsku, setFnsku] = useState("");
  const [sku, setSku] = useState("");
  const [expectedCount, setExpectedCount] = useState<number | string>("");
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState<NeedsReviewItem | null>(
    null
  );
  const selectedChildren = useRef<NeedsReviewItem[]>([]);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(2);
  const [matchItem, setMatchItem] = useState<NeedsReviewItem | null>(null);
  const [checjkJanItem, setCheckJanItem] = useState<NeedsReviewItem[]>([]);
  const [imageUrl, setImageUrl] = useState("");
  const [janDialogOpen, setJanDialogOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);
  const [checkFnskuCode, setCheckFnskuCode] = useState("");
  const [checkJanCode, setCheckJanCode] = useState("");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const refSortKey = useRef<number>(1);
  const refDirection = useRef<number>(-1);

  // チェックされたIDの更新ハンドラ
  const handleCheckChange = (newChecked: Set<string>) => {
    setCheckedIds(newChecked);
  };

  const [selectedGenreId, setSelectedGenreId] = useState(
    searchGenres[0]?.label ?? ""
  );

  const resetGenreWord = () => {
    setJanCode("");
    setAsin("");
    setItemName("");
    setFnsku("");
    setSku("");
    setSearchKeyword("");
    return new Promise<void>((resolve) => {
      setJanCode("");
      setAsin("");
      setItemName("");
      setFnsku("");
      setSku("");
      setSearchKeyword("");

      // 状態更新の直後に resolve（1フレーム後に確実に）
      requestAnimationFrame(() => resolve());
    });
  };

  const resetFilter = async () => {
    if (isNotGeneralUser(loginUser?.permissionId)) {
      setSelectedUserId(null);
    }
    await resetGenreWord();
    setNeedsReviewUpdatedFlag(true);
    return new Promise<void>((resolve) => {
      if (isNotGeneralUser(loginUser?.permissionId)) {
        setSelectedUserId(null);
      }
    });
  };

  const handleGenreChange = (genreId: string) => {
    setSelectedGenreId(genreId);
    resetGenreWord();
  };

  const resetCheckCode = () => {
    setCheckFnskuCode("");
    setCheckJanCode("");
  };

  const transformData = (data: ResponseAmazonItems[]): NeedsReviewItem[] => {
    const grouped = new Map<number, ResponseAmazonItems[]>();
    // amazon_item_id ごとにグルーピング
    data.forEach((item) => {
      const key = item.amazonItemId!;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    });

    const result: NeedsReviewItem[] = [];

    grouped.forEach((items, amazonItemId) => {
      // グループ内に親データ相当の1行があると仮定（amazon_接頭辞のカラムが使える）
      const first = items[0];

      if (items.length === 1) {
        // 子のみの場合（親なし）
        const setNum = first.amazonSetNum ?? 0;
        const arriveNum = first.amazonArriveNum ?? 0;
        result.push({
          id: `only-${amazonItemId}`,
          isParent: true,
          inputDate: formatDate(first.inputDate),
          user: first.userName,
          itemName: first.itemName,
          janCode: first.janCode,
          expiryDate: formatDate(first.parentExpiryDate),
          setCount: setNum,
          deliveryCount: arriveNum,
          deliveryUnit: first.deliveryUnit,
          expectedCount: setNum * arriveNum,
          shippedCount: first.amazonActualShippingNum,
          receivedCount: first.amazonSumNum,
          asin: first.asin,
          sku: first.sku,
          fnsku: first.fnskuCode,
          stickerNum: first.stickerNum,
          returnNum: first.returnNum,
          destructionNum: first.destructionNum,
          storageLocation: first.storageLocation,
          statusId: first.statusId,
          baseId: Number(first.baseId ?? 0),
          userId: Number(first.userId ?? 0),
          memo: first.memo,
          image: first.imageUrl,
        });
      } else {
        // 親ありの場合
        const setNum = first.amazonSetNum ?? 0;
        const arriveNum = first.amazonArriveNum ?? 0;
        result.push({
          id: `parent-${amazonItemId}`,
          isParent: true,
          inputDate: formatDate(first.inputDate),
          user: first.userName,
          itemName: first.amazonItemName ?? "",
          janCode: "", // 空欄
          expiryDate: formatDate(first.parentExpiryDate),
          setCount: setNum,
          deliveryCount: arriveNum,
          deliveryUnit: first.deliveryUnit,
          expectedCount: setNum * arriveNum,
          shippedCount: first.amazonActualShippingNum,
          receivedCount: first.amazonSumNum,
          asin: first.asin,
          sku: first.sku,
          fnsku: first.fnskuCode,
          stickerNum: first.stickerNum,
          returnNum: first.returnNum,
          destructionNum: first.destructionNum,
          storageLocation: first.storageLocation,
          statusId: first.statusId,
          baseId: Number(first.baseId ?? 0),
          userId: Number(first.userId ?? 0),
          memo: first.memo,
          image: first.imageUrl,
        });

        // 子データ（通常カラム）
        items.forEach((child, idx) => {
          const setNum = child.setNum ?? 0;
          const arriveNum = child.arriveNum ?? 0;
          result.push({
            id: `child-${amazonItemId}-${idx}`,
            parentId: `parent-${amazonItemId}`,
            inputDate: "",
            user: child.userName,
            itemName: child.itemName,
            janCode: child.janCode,
            expiryDate: formatDate(child.expiryDate),
            setCount: setNum,
            deliveryCount: arriveNum,
            expectedCount: setNum * arriveNum,
            shippedCount: child.actualShippingNum,
            receivedCount: child.sumNum,
            asin: child.asin,
            sku: child.sku,
            fnsku: child.fnskuCode,
            stickerNum: child.stickerNum,
            returnNum: child.returnNum,
            destructionNum: child.destructionNum,
            storageLocation: child.storageLocation,
            baseId: Number(first.baseId ?? 0),
            userId: Number(first.userId ?? 0),
          });
        });
      }
    });

    return result;
  };

  const handleParentCheck = (parentId: string, childIds: string[]) => {
    const newSet = new Set<string>([parentId, ...childIds]);
    handleCheckChange(newSet);
  };

  const handleKeywordChange = (keyword: string) => {
    // 一致する親行を探す（parentId が undefined or isParent が true のもの）
    const matchedParent = needsReviewList.find(
      (item) => item.janCode === keyword && item.isParent
    );

    if (matchedParent && selectedGenreId === searchGenres[0].id) {
      searchItems(keyword);
      inputRef.current?.focus();

      // 対象親の子IDリストを取得
      const childIds = needsReviewList
        .filter((child) => child.parentId === matchedParent.id)
        .map((child) => String(child.id));

      // handleParentCheck でチェック更新（関数を PendingReceipt で定義する）
      handleParentCheck(String(matchedParent.id), childIds);
    } else {
      // 該当なしならチェックをすべて解除
      handleCheckChange(new Set());
    }
  };

  const focusInputField = () => {
    inputRef.current?.focus();
  };

  const handleRowClick = (item: NeedsReviewItem) => {
    if (!item.isParent) return;

    const children = needsReviewList.filter((row) => row.parentId === item.id);
    console.log(item);
    setSelectedParent(item);
    selectedChildren.current = children;
    setDetailDialogOpen(true);
  };

  const handleStatusChange = (id: number) => {
    setSelectedStatusId(id);
  };

  const handleUserChange = (userId: number | null) => {
    setSelectedUserId(userId);
    // ここでフィルタ処理やAPI呼び出し
  };

  const userOptions = useMemo(() => {
    return (
      userSearchOptions?.map((user) => ({
        label: `${user.userNumber ?? ""}_${user.lastName ?? ""}${
          user.firstName ?? ""
        }`,
        id: user.id!,
      })) ?? []
    );
  }, [userSearchOptions]);

  const fetchData = async (req: RequestGetAmazonItemsListRequest) => {
    const response = await fetchAmazonItems(req);
    if (response) {
      const transformed = transformData(response.items ?? []);
      setTotalListCount(response.totalCount ?? 0);
      setNeedsReviewList(transformed);
    }
  };

  useEffect(() => {
    if (loginUser) {
      if (loginUser.tenantId && loginUser.id) {
        const req: RequestGetAmazonItemsListRequest = {
          statusId: Status.NeedsReview,
          tenantId: loginUser.tenantId,
          userId: loginUser.id,
          createdUserId:
            isGeneralUser(loginUser?.permissionId) ? loginUser.id! : undefined,
          page,
          pageSize: limit,
          sortKey: refSortKey.current,
          direction: refDirection.current,
        };
        fetchData(req);
      }
      if (isGeneralUser(loginUser?.permissionId)) {
        setSelectedUserId(loginUser.id!);
      }
    }
  }, [loginUser]);

  useEffect(() => {
    if (needsReviewUpdatedFlag) {
      if (loginUser?.tenantId && loginUser.id) {
        const req: RequestGetAmazonItemsListRequest = {
          statusId: Status.NeedsReview,
          tenantId: loginUser.tenantId,
          userId: loginUser.id,
          createdUserId:
            isGeneralUser(loginUser?.permissionId) ? loginUser.id! : undefined,
          page,
          pageSize: limit,
          sortKey: refSortKey.current,
          direction: refDirection.current,
        };
        fetchData(req);
      }
      resetNeedsReviewUpdatedFlag();
    }
  }, [needsReviewUpdatedFlag]);

  useEffect(() => {
    switch (selectedGenreId) {
      case searchGenres[0].label:
        setJanCode(searchKeyword);
        break;
      case searchGenres[1].label:
        setAsin(searchKeyword);
        break;
      case searchGenres[2].label:
        setItemName(searchKeyword);
        break;
      case searchGenres[3].label:
        setFnsku(searchKeyword);
        break;
      case searchGenres[4].label:
        setSku(searchKeyword);
        break;
    }
    handleKeywordChange(searchKeyword);
  }, [searchKeyword]);

  const searchItems = (searchKeyword: string) => {
    if (loginUser?.tenantId && loginUser.id) {
      const req: RequestGetAmazonItemsListRequest = {
        statusId: Status.NeedsReview,
        tenantId: loginUser.tenantId,
        userId: loginUser.id,
        createdUserId: selectedUserId ?? undefined,
        janCode:
          searchGenres[0].id && janCode !== "" ? searchKeyword : undefined,
        asin: searchGenres[1].id && asin !== "" ? searchKeyword : undefined,
        amazonItemName:
          searchGenres[2].id && itemName !== "" ? searchKeyword : undefined,
        fnskuCode:
          searchGenres[3].id && fnsku !== "" ? searchKeyword : undefined,
        sku: searchGenres[4].id && sku !== "" ? searchKeyword : undefined,
        page,
        pageSize: limit,
        sortKey: refSortKey.current,
        direction: refDirection.current,
      };
      fetchData(req);
    }
  };

  const handlePageChange = (newPage: number, newLimit: number) => {
    if (loginUser?.tenantId && loginUser.id) {
      setPage(newPage);
      setLimit(newLimit);

      const req: RequestGetAmazonItemsListRequest = {
        statusId: Status.NeedsReview,
        tenantId: loginUser.tenantId,
        userId: loginUser.id,
        createdUserId: isGeneralUser(loginUser.permissionId) ? loginUser.id : undefined,
        janCode:
          searchGenres[0].id && janCode !== "" ? searchKeyword : undefined,
        asin: searchGenres[1].id && asin !== "" ? searchKeyword : undefined,
        amazonItemName:
          searchGenres[2].id && itemName !== "" ? searchKeyword : undefined,
        fnskuCode:
          searchGenres[3].id && fnsku !== "" ? searchKeyword : undefined,
        sku: searchGenres[4].id && sku !== "" ? searchKeyword : undefined,
        page: newPage + 1,
        pageSize: newLimit,
        sortKey: refSortKey.current,
        direction: refDirection.current,
      };
      fetchData(req);
    }
  };

  const handleExpiryDatePrintAllAtOnce = () => {
    const selectedIds: number[] = Array.from(checkedIds).map((id) =>
      Number(String(id).split("-")[1])
    );
    const printTargets: ExpirationLabelItem[] = needsReviewList
      .filter(
        (row) =>
          row.isParent &&
          selectedIds.includes(Number(String(row.id).split("-")[1])) &&
          !!row.expiryDate
      )
      .map((row) => {
        const date = new Date(row.expiryDate!);
        const formatted = format(date, "yyyy年MM月dd日");
        return {
          expirationDate: formatted,
          count: (row.setCount ?? 1) * (row.deliveryCount ?? 1),
        };
      });

    openPrintExpirationLabelWindow(printTargets);
  };

  const handlePrintAllAtOnce = () => {
    const selectedIds: number[] = Array.from(checkedIds).map((id) =>
      Number(String(id).split("-")[1])
    );
    const printTargets: PrintLabelItem[] = needsReviewList
      .filter(
        (row) =>
          row.isParent &&
          selectedIds.includes(Number(String(row.id).split("-")[1]))
      )
      .map((row) => ({
        fnsku: toDisplayFnsku(row.fnsku),
        itemName: row.itemName ?? "",
        count: row.deliveryCount ?? 1,
      }));

    // 親行が 1 つも見つからない場合はスキップ
    if (printTargets.length > 0) {
      const sanitized = printTargets.map((item) => ({
        fnsku: String(item.fnsku ?? ""),
        itemName: String(item.itemName ?? ""),
        count: Number(item.count ?? 1),
      }));
      openPrintLabelsWindow(sanitized);
    }
  };

  const changeSortKey = (
    key: keyof NeedsReviewItem | null,
    direction: Order
  ) => {
    if (loginUser?.tenantId && loginUser.id) {
      // --- ソートキーをPendingReceiptItemのkeyから対応するSortKey番号に変換 ---
      const sortKeyMap: Record<keyof NeedsReviewItem, number> = {
        inputDate: 1, // a.created_at
        user: 2, // user_name
        itemName: 3, // a.amazon_item_name
        janCode: 4, // i.jan_code
        expiryDate: 5, // i.expiry_date
        setCount: 6, // i.set_num
        deliveryCount: 7, // i.arrive_num
        expectedCount: 8, // i.set_num * i.arrive_num
        receivedCount: 9, // a.sum_num
        fnsku: 10, // a.fnsku_code

        // 他のフィールドは対象外
        id: 0,
        parentId: 0,
        isParent: 0,
        image: 0,
        asin: 0,
        sku: 0,
        stickerNum: 0,
        returnNum: 0,
        destructionNum: 0,
        storageLocation: 0,
        baseId: 0,
        userId: 0,
        memo: 0,
        statusId: 0,
        rowColor: 0,
        isChecked: 0,
        deliveryUnit: 0,
        shippedCount: 0,
      };

      const sortKey = key ? sortKeyMap[key] ?? 1 : 1;
      const sortDirection = direction === "asc" ? 1 : -1;
      refSortKey.current = sortKey;
      refDirection.current = sortDirection;
      const req: RequestGetAmazonItemsListRequest = {
        statusId: Status.NeedsReview,
        tenantId: loginUser.tenantId,
        userId: loginUser.id,
        createdUserId: selectedUserId ?? undefined,
        janCode:
          searchGenres[0].id && janCode !== "" ? searchKeyword : undefined,
        asin: searchGenres[1].id && asin !== "" ? searchKeyword : undefined,
        amazonItemName:
          searchGenres[2].id && itemName !== "" ? searchKeyword : undefined,
        fnskuCode:
          searchGenres[3].id && fnsku !== "" ? searchKeyword : undefined,
        sku: searchGenres[4].id && sku !== "" ? searchKeyword : undefined,
        page,
        pageSize: limit,
        sortKey,
        direction: sortDirection,
      };
      fetchData(req);
    }
  };

  return {
    inputRef,
    loginUser,
    needsReviewList,
    userOptions,
    selectedUserId,
    handleUserChange,
    checkedIds,
    handleCheckChange,
    searchGenres,
    selectedGenreId,
    setSelectedGenreId,
    focusInputField,
    handleGenreChange,
    searchKeyword,
    setSearchKeyword,
    searchItems,
    resetFilter,
    resetGenreWord,
    handleKeywordChange,
    expectedCount,
    handleRowClick,
    selectedStatusId,
    handleStatusChange,
    detailDialogOpen,
    setDetailDialogOpen,
    selectedParent,
    setSelectedParent,
    selectedChildren,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    resetCheckCode,
    janDialogOpen,
    setJanDialogOpen,
    checjkJanItem,
    imageUrl,
    handleExpiryDatePrintAllAtOnce,
    handlePrintAllAtOnce,
    handlePageChange,
    totalListCount,
    changeSortKey,
  };
};
